import { Component, OnDestroy, OnInit, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { AuthService, BroadcastService, GlobalConfigService, LogService, MibpLogger } from 'root/services';
import { DialogButton } from '..';
import { DialogButtonType } from 'root/shared/components/dialogs/dialog/dialog-button.enum';
import { DialogComponent } from 'root/shared/components/dialogs/dialog/dialog.component';
import { differenceInSeconds } from 'date-fns';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { User } from 'root/mibp-openapi-gen/models';


@Component({
  selector: 'mibp-session-timeout',
  templateUrl: './session-timeout.component.html',
  styleUrls: ['./session-timeout.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, DialogComponent]
})
export class SessionTimeoutComponent implements OnInit, OnDestroy {
  private idle = inject(Idle);
  authService = inject(AuthService);
  private broadcast = inject(BroadcastService);

  idleCountDown: number;
  dialogButtons: DialogButton[] = [
    { resourceKey: 'Global_Logout', id: 'sign_out', type: DialogButtonType.Secondary },
    { resourceKey: 'Global_StayLoggedIn', id: 'stay', type: DialogButtonType.Primary }
  ];
  @ViewChild('dialog') dialog: DialogComponent;

  countdownTimer?: number;
  countdownStart?: Date;

  onIdleStartSubscription?: Subscription;
  authSubscription?: Subscription;
  disabledSubscription?: Subscription;

  popupShowAfterSeconds: number;
  popupLogoutAfterSeconds: number;

  private log = inject(LogService).withPrefix('session-timeout.component');

  constructor()
  {
    const logService = inject(LogService);
    const globalConfig = inject(GlobalConfigService);

    this.popupShowAfterSeconds = globalConfig.idleShowPopupAfterSeconds;
    this.popupLogoutAfterSeconds = globalConfig.idlePopupLogoutAfterSeconds;
  }

  ngOnInit(): void {

    this.idle.setIdle(this.popupShowAfterSeconds);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.onIdleStartSubscription = this.idle.onIdleStart.subscribe(() => {

      if(!this.authService.isLoggedIn())
      {
        this.idle.stop();
        if (this.countdownTimer) {
          clearInterval(this.countdownTimer);
          this.countdownTimer = undefined;
        }
        this.dialog?.close?.();
        return;
      }

      // User is Idle. we turn of ng-idle and handle the countdown ourselves
      this.idle.stop();
      this.countdownTimer = window.setInterval(() => this.countdownTick(), 450);
      this.countdownStart = new Date();
      this.idleCountDown = this.popupLogoutAfterSeconds;
      this.dialog.open();
    });

    this.authSubscription = this.authService.isLoggedInSubject.subscribe(isLoggedIn=> {
      if (isLoggedIn) {
        this.idle.watch();
      } else {
        this.idle.stop();
      }
    });

    // We can use broadcast service if we temporarily want to disable this
    // For example during long-running exports
    this.disabledSubscription = this.broadcast.sessionTimeoutDisabled.subscribe(isDisabled=> {
      if (isDisabled && this.idle.isRunning()) {
        this.log.info('Session timer was disabled from broadcast');
        clearInterval(this.countdownTimer);
        this.idle.stop();
      } else if (!isDisabled && !this.idle.isRunning()) {
        this.log.info('Session timer was re-enabled from broadcast');
        this.idle.watch();
      }
    });
  }

  /**
   * Is invoked while dialog is open in order to show the countdown
   */
  countdownTick(): void {
    const diff = differenceInSeconds(new Date(), this.countdownStart);
    const timeRemaining = this.popupLogoutAfterSeconds - diff;

    if (this.idleCountDown <= 0) {
      // Time is up. Log the user out!
      clearInterval(this.countdownTimer);
      this.logout(true);
    } else {
      this.idleCountDown = timeRemaining;
    }
  }

  dialogClick(e: DialogButton): void {
    if (e.id == 'stay') {
      this.stay();
    } else {
      this.logout();
    }
  }

  logout(automatic = false): void {
    this.dialog.close();
    this.authService.signout(null, automatic ? 'idle' : null);
    this.idle.stop();
  }

  stay(): void {
    this.dialog.close();
    this.reset();
  }

  reset(): void {
    clearInterval(this.countdownTimer);

    if (this.idle.isRunning()) {
      this.idle.stop();
    }

    this.idle.watch();
  }

  ngOnDestroy(): void {
    this.authService?.isLoggedInSubject.unsubscribe();
    this.authSubscription?.unsubscribe();
    this.onIdleStartSubscription?.unsubscribe();
  }
}
